// src/pages/drive/pdfViewer/EmployeeFilePDFViewer.tsx

import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css'; // Optional: for text selection
import { Button, Form } from 'react-bootstrap';
import './PDFViewer.css'; // Reusing your existing CSS

// Set worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface EmployeeFilePDFViewerProps {
  fileUrl: string;
  scale?: number;
}

const EmployeeFilePDFViewer: React.FC<EmployeeFilePDFViewerProps> = ({ 
  fileUrl, 
  scale = 1.0 
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState<string>("1");

  // Reset page when file changes
  useEffect(() => {
    setCurrentPage(1);
    setPageInput("1");
  }, [fileUrl]);

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInput, 10);
    if (!isNaN(pageNumber) && numPages && pageNumber >= 1 && pageNumber <= numPages) {
      setCurrentPage(pageNumber);
    } else {
      setPageInput(currentPage.toString());
    }
  };

  const handlePageInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePageInputBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div>
      {/* Pagination Controls */}
      <div className="flex gap-2 mb-4 justify-start items-center">
        {numPages && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline-secondary"
              className="btn_sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <div className="flex items-center">
              <Form.Control
                type="number"
                value={pageInput}
                onChange={handlePageInputChange}
                onBlur={handlePageInputBlur}
                onKeyPress={handlePageInputKeyPress}
                className="w-16 text-center form-control-sm"
                min="1"
                max={numPages}
              />
              <span className="text-sm min-w-10 text-gray-500 ml-1 flex flex-col">
                {"/ " + numPages}
              </span>
            </div>
            <Button
              variant="outline-secondary"
              className="btn_sm"
              onClick={() => numPages && setCurrentPage((p) => Math.min(numPages, p + 1))}
              disabled={!numPages || currentPage >= numPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* PDF Document Renderer */}
      <div className='pdfViewer' style={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
        {fileUrl && (
          <Document 
            className='bg-secondary dark:bg-secondary-dark flex flex-col items-center' 
            file={fileUrl} 
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<div className="p-4">Loading PDF...</div>}
            error={<div className="p-4 text-red-500">Failed to load PDF.</div>}
          >
            <Page
              className='bg-white shadow-md'
              pageNumber={currentPage}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              scale={scale} 
            />
          </Document>
        )}
      </div>
    </div>
  );
};

export default EmployeeFilePDFViewer;